import React from 'react';
import {AlertCircle, Database, Lock, Mail, MapPin, Phone, Shield} from 'lucide-react';

const Privacy = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 py-12">
                <div className="bg-white rounded-lg shadow-sm p-8">
                    <div className="flex items-center mb-8">
                        <Shield className="w-10 h-10 text-blue-600 mr-3"/>
                        <h1 className="text-3xl font-bold text-gray-900">Politique de Confidentialité</h1>
                    </div>

                    <p className="text-gray-600 mb-8">
                        Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
                    </p>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introduction</h2>
                        <p className="text-gray-700 mb-4">
                            La protection de vos données personnelles est au cœur de nos préoccupations. Cette politique
                            de confidentialité
                            explique comment nous collectons, utilisons, stockons et protégeons vos données personnelles
                            conformément au
                            Règlement Général sur la Protection des Données (RGPD).
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Responsable du Traitement</h2>
                        <div className="bg-blue-50 p-6 rounded-lg">
                            <p className="text-gray-700 mb-2">
                                <strong>Société :</strong> mesprocedures.fr
                            </p>
                            <p className="text-gray-700 mb-2">
                                <strong>Adresse :</strong> 47 avenue du Docteur Arnold Netter 75012
                            </p>
                            <p className="text-gray-700 mb-2">
                                <strong>Email :</strong> contact@mesprocedures.fr
                            </p>
                            <p className="text-gray-700">
                                <strong>Téléphone :</strong> +33 1 89 71 59 61
                            </p>
                        </div>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Notre Engagement</h2>
                        <div className="flex items-start mb-4">
                            <AlertCircle className="w-6 h-6 text-green-600 mr-3 mt-1"/>
                            <p className="text-gray-700">
                                Nous ne vendons, ne louons, ni ne commercialisons vos données personnelles à des tiers.
                                Vos informations
                                sont strictement utilisées dans le cadre de notre relation client et pour améliorer nos
                                services.
                            </p>
                        </div>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Données Collectées</h2>
                        <p className="text-gray-700 mb-4">
                            Nous collectons uniquement les données nécessaires pour :
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-gray-700">
                            <li>Vous contacter afin de répondre à vos demandes et trouver des solutions à vos
                                problèmes
                            </li>
                            <li>Gérer votre compte client et assurer le suivi de vos dossiers</li>
                            <li>Améliorer nos services et votre expérience utilisateur</li>
                            <li>Respecter nos obligations légales et réglementaires</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Types de Données Collectées</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <Mail className="w-5 h-5 text-blue-600 mr-2"/>
                                    <h3 className="font-semibold text-gray-800">Données d'Identification</h3>
                                </div>
                                <ul className="list-disc pl-6 text-gray-700">
                                    <li>Nom et prénom</li>
                                    <li>Adresse email</li>
                                    <li>Numéro de téléphone</li>
                                    <li>Adresse postale</li>
                                </ul>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <Database className="w-5 h-5 text-blue-600 mr-2"/>
                                    <h3 className="font-semibold text-gray-800">Données de Navigation</h3>
                                </div>
                                <ul className="list-disc pl-6 text-gray-700">
                                    <li>Adresse IP</li>
                                    <li>Type de navigateur</li>
                                    <li>Pages visitées</li>
                                    <li>Données de cookies</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Durée de Conservation</h2>
                        <p className="text-gray-700 mb-4">
                            Vos données personnelles sont conservées :
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-gray-700">
                            <li>Pendant toute la durée de notre relation commerciale</li>
                            <li>3 ans après notre dernier contact pour les prospects</li>
                            <li>10 ans pour les documents comptables (obligation légale)</li>
                            <li>Jusqu'à exercice de votre droit à l'effacement (sauf obligations légales)</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Sécurité des Données</h2>
                        <div className="flex items-start mb-4">
                            <Lock className="w-6 h-6 text-green-600 mr-3 mt-1"/>
                            <p className="text-gray-700">
                                Nous mettons en œuvre toutes les mesures techniques et organisationnelles nécessaires
                                pour assurer
                                la sécurité et la confidentialité de vos données personnelles, notamment :
                            </p>
                        </div>
                        <ul className="list-disc pl-6 space-y-2 text-gray-700">
                            <li>Chiffrement des données sensibles</li>
                            <li>Accès restreint aux données personnelles</li>
                            <li>Protocoles de sécurité régulièrement mis à jour</li>
                            <li>Formation du personnel sur la protection des données</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Vos Droits</h2>
                        <p className="text-gray-700 mb-4">
                            Conformément au RGPD, vous disposez des droits suivants :
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border border-gray-200 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">Droit d'accès</h3>
                                <p className="text-gray-700">Obtenir la confirmation que vos données sont traitées et
                                    accéder à ces données</p>
                            </div>
                            <div className="border border-gray-200 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">Droit de rectification</h3>
                                <p className="text-gray-700">Corriger vos données personnelles si elles sont inexactes
                                    ou incomplètes</p>
                            </div>
                            <div className="border border-gray-200 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">Droit à l'effacement</h3>
                                <p className="text-gray-700">Demander la suppression de vos données dans certaines
                                    circonstances</p>
                            </div>
                            <div className="border border-gray-200 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">Droit à la portabilité</h3>
                                <p className="text-gray-700">Recevoir vos données dans un format structuré et
                                    lisible</p>
                            </div>
                            <div className="border border-gray-200 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">Droit d'opposition</h3>
                                <p className="text-gray-700">Vous opposer au traitement de vos données pour des motifs
                                    légitimes</p>
                            </div>
                            <div className="border border-gray-200 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">Droit de limitation</h3>
                                <p className="text-gray-700">Demander la limitation du traitement dans certains cas</p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Exercer Vos Droits</h2>
                        <p className="text-gray-700 mb-4">
                            Pour exercer vos droits, vous pouvez nous contacter :
                        </p>
                        <div className="bg-gray-50 p-6 rounded-lg">
                            <div className="flex items-center mb-3">
                                <Mail className="w-5 h-5 text-blue-600 mr-2"/>
                                <p className="text-gray-700">Par email : contact@mesprocedures.fr</p>
                            </div>
                            <div className="flex items-center mb-3">
                                <Phone className="w-5 h-5 text-blue-600 mr-2"/>
                                <p className="text-gray-700">Par téléphone : +33 1 89 71 59 61</p>
                            </div>
                            <div className="flex items-center">
                                <MapPin className="w-5 h-5 text-blue-600 mr-2"/>
                                <p className="text-gray-700">Par courrier : 47 avenue du Docteur Arnold Netter 75012</p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Cookies</h2>
                        <p className="text-gray-700 mb-4">
                            Notre site utilise des cookies pour améliorer votre expérience de navigation. Vous pouvez
                            configurer votre navigateur pour refuser les cookies ou être alerté lorsqu'un cookie est
                            envoyé.
                        </p>
                    </section>


                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Modifications</h2>
                        <p className="text-gray-700">
                            Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment.
                            Les modifications entreront en vigueur dès leur publication sur notre site. Nous vous
                            encourageons à consulter régulièrement cette page pour prendre connaissance des éventuelles
                            modifications.
                        </p>
                    </section>


                </div>
            </div>
        </div>
    );
};

export default Privacy;